/**
 * The rules of tents, ported from `backend/apps/puzzletypes/tents/rules.py` (parity through the
 * shared fixtures), plus the live contradictions, which only the client computes.
 *
 * A grid is a list of row strings. Nothing here ever sees the answer key.
 */
import { EMPTY, TENT, TREE, type Axis, type Cell, type Count } from "./types";

const ORTHOGONAL: Cell[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];
const AROUND: Cell[] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

export const key = (r: number, c: number): string => `${r},${c}`;

export function cellsOf(rows: string[], char: string): Cell[] {
  const found: Cell[] = [];
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1) if (row[c] === char) found.push([r, c]);
  });
  return found;
}

/** The most tents a line of `length` cells can hold without two touching. */
export function maxTents(length: number): number {
  return Math.floor((length + 1) / 2);
}

/** Every tent with another tent among its eight neighbours, as `r,c` keys. */
export function touching(tents: Cell[]): Set<string> {
  const all = new Set(tents.map(([r, c]) => key(r, c)));
  return new Set(
    tents
      .filter(([r, c]) => AROUND.some(([dr, dc]) => all.has(key(r + dr, c + dc))))
      .map(([r, c]) => key(r, c)),
  );
}

/** Whether every tree can be paired with its own orthogonally adjacent tent, and back. */
export function hasPerfectMatching(trees: Cell[], tents: Cell[]): boolean {
  if (trees.length !== tents.length) return false;
  const tentSet = new Set(tents.map(([r, c]) => key(r, c)));
  const owner = new Map<string, Cell>();
  const augment = (tree: Cell, seen: Set<string>): boolean => {
    for (const [dr, dc] of ORTHOGONAL) {
      const tent = key(tree[0] + dr, tree[1] + dc);
      if (!tentSet.has(tent) || seen.has(tent)) continue;
      seen.add(tent);
      const holder = owner.get(tent);
      if (!holder || augment(holder, seen)) {
        owner.set(tent, tree);
        return true;
      }
    }
    return false;
  };
  return trees.every((tree) => augment(tree, new Set()));
}

export function lineCounts(
  tents: Cell[],
  rows: number,
  cols: number,
): { rows: number[]; cols: number[] } {
  const counts = { rows: Array<number>(rows).fill(0), cols: Array<number>(cols).fill(0) };
  for (const [r, c] of tents) {
    if (r < rows) counts.rows[r] += 1;
    if (c < cols) counts.cols[c] += 1;
  }
  return counts;
}

/** Every known count that differs from the tents on its line. */
export function countMismatches(
  counts: { rows: Count[]; cols: Count[] },
  tents: Cell[],
  rows: number,
  cols: number,
): { line: Axis; index: number }[] {
  const actual = lineCounts(tents, rows, cols);
  const found: { line: Axis; index: number }[] = [];
  counts.rows.forEach((n, index) => {
    if (n !== null && n !== (actual.rows[index] ?? 0)) found.push({ line: "row", index });
  });
  counts.cols.forEach((n, index) => {
    if (n !== null && n !== (actual.cols[index] ?? 0)) found.push({ line: "col", index });
  });
  return found;
}

/** Every rule the tents break, as the server's codes; empty when the answer is right. */
export function issues(
  trees: string[],
  counts: { rows: Count[]; cols: Count[] },
  tentRows: string[],
): string[] {
  const rows = trees.length;
  const cols = trees[0]?.length ?? 0;
  const treeCells = cellsOf(trees, TREE);
  const tents = cellsOf(tentRows, TENT);
  const treeKeys = new Set(treeCells.map(([r, c]) => key(r, c)));
  const found: string[] = [];
  if (tents.some(([r, c]) => treeKeys.has(key(r, c)))) found.push("tent_on_tree");
  if (touching(tents).size > 0) found.push("tents_touch");
  if (countMismatches(counts, tents, rows, cols).length > 0) found.push("tent_count_mismatch");
  if (!hasPerfectMatching(treeCells, tents)) found.push("tents_unpaired");
  return found;
}

/** Line keys, `row:3` or `col:0`, used by the board and the fixtures alike. */
export function lineKey(axis: Axis, index: number): string {
  return `${axis}:${index}`;
}

export interface Contradictions {
  /** Tents touching another tent, or with no tree beside them. */
  tents: Set<string>;
  /** Trees whose four neighbours are all grass, trees or the border: no tent can reach them. */
  trees: Set<string>;
  /** Lines with more tents than their count, or too few free cells left to reach it. */
  lines: Set<string>;
}

/**
 * What the board can no longer be, from the trees, the counts and the marks (`A`, `x`, `.`)
 * alone. Sound, not complete: every flag is a real mistake, and whatever needs reasoning
 * beyond one tent, one tree or one line stays silent (CLAUDE.md §16.4).
 */
export function contradictions(
  trees: string[],
  counts: { rows: Count[]; cols: Count[] },
  marks: string[],
): Contradictions {
  const rows = trees.length;
  const cols = trees[0]?.length ?? 0;
  const isTree = (r: number, c: number) => trees[r]?.[c] === TREE;
  const at = (r: number, c: number) => (isTree(r, c) ? TREE : (marks[r]?.[c] ?? EMPTY));
  const tents = cellsOf(marks, TENT).filter(([r, c]) => !isTree(r, c));

  const flaggedTents = touching(tents);
  for (const [r, c] of tents) {
    if (!ORTHOGONAL.some(([dr, dc]) => isTree(r + dr, c + dc))) flaggedTents.add(key(r, c));
  }

  const flaggedTrees = new Set<string>();
  for (const [r, c] of cellsOf(trees, TREE)) {
    const open = ORTHOGONAL.some(([dr, dc]) => {
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || cc < 0 || rr >= rows || cc >= cols) return false;
      const mark = at(rr, cc);
      return mark === TENT || mark === EMPTY;
    });
    if (!open) flaggedTrees.add(key(r, c));
  }

  const lines = new Set<string>();
  const check = (axis: Axis, index: number, count: Count, cells: Cell[]) => {
    if (count === null) return;
    const placed = cells.filter(([r, c]) => at(r, c) === TENT).length;
    const open = cells.filter(([r, c]) => at(r, c) === EMPTY).length;
    if (placed > count || placed + open < count) lines.add(lineKey(axis, index));
  };
  counts.rows.forEach((count, r) =>
    check(
      "row",
      r,
      count,
      Array.from({ length: cols }, (_, c): Cell => [r, c]),
    ),
  );
  counts.cols.forEach((count, c) =>
    check(
      "col",
      c,
      count,
      Array.from({ length: rows }, (_, r): Cell => [r, c]),
    ),
  );
  return { tents: flaggedTents, trees: flaggedTrees, lines };
}

/** Lines whose known count is met: their numbers step back. */
export function doneLines(
  trees: string[],
  counts: { rows: Count[]; cols: Count[] },
  marks: string[],
): Set<string> {
  const tents = cellsOf(marks, TENT).filter(([r, c]) => trees[r]?.[c] !== TREE);
  const actual = lineCounts(tents, trees.length, trees[0]?.length ?? 0);
  const done = new Set<string>();
  counts.rows.forEach((n, i) => n !== null && n === actual.rows[i] && done.add(lineKey("row", i)));
  counts.cols.forEach((n, i) => n !== null && n === actual.cols[i] && done.add(lineKey("col", i)));
  return done;
}

/** The counts a set of tents implies, for the author's "count from the tents". */
export function countsOf(
  tentRows: string[],
  rows: number,
  cols: number,
): { rows: Count[]; cols: Count[] } {
  return lineCounts(cellsOf(tentRows, TENT), rows, cols);
}
